
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { initializeBluetooth } from './services/nativeBluetooth'

// Add more detailed debug logging
console.log("HRV Booster app starting up...");
console.log("Environment:", import.meta.env.MODE);
console.log("Browser/Device:", navigator.userAgent);
console.log("Capacitor Platform:", Capacitor.getPlatform());
console.log("Is Native Platform:", Capacitor.isNativePlatform());

// Check if running on iOS - fix TypeScript error with type assertion
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
console.log("Is iOS device:", isIOS);

// Log DOM element availability
console.log("Root element exists:", !!document.getElementById('root'));

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 30, // 30 minutes
      gcTime: 1000 * 60 * 120,   // 2 hours
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Global error handler for unhandled exceptions
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error || event.message);
  
  // You could log to an error tracking service here
  
  // In production, try to recover by refreshing the page if multiple errors occur
  if (import.meta.env.PROD) {
    const errorCount = parseInt(sessionStorage.getItem('errorCount') || '0');
    sessionStorage.setItem('errorCount', (errorCount + 1).toString());
    
    // If we've seen 3+ errors in this session, try a page refresh
    if (errorCount >= 3) {
      sessionStorage.setItem('errorCount', '0');
      console.log('Too many errors, reloading page...');
      // Add a delay to prevent refresh loops
      setTimeout(() => window.location.reload(), 5000);
    }
  }
});

// Initialize Bluetooth with fallback
const initializeApp = async () => {
  try {
    // Attempt to initialize Bluetooth with fallback
    const bluetoothStatus = await initializeBluetooth();
    console.log("Bluetooth initialization status:", bluetoothStatus);
    
    // Add bluetooth status to window for debugging and use in components
    (window as any).bluetoothStatus = bluetoothStatus;
    
    // Even if Bluetooth fails, we'll still mount the app
    mountApp();
  } catch (error) {
    console.error("Error initializing Bluetooth, but we'll continue anyway:", error);
    // Still mount the app even if Bluetooth initialization fails
    mountApp();
  }
};

// Create a safer mount function with better error handling
const mountApp = () => {
  const rootElement = document.getElementById('root');
  
  if (!rootElement) {
    console.error("Root element not found. Cannot mount app.");
    // Try to create a root element as a fallback
    const fallbackRoot = document.createElement('div');
    fallbackRoot.id = 'root';
    document.body.appendChild(fallbackRoot);
    console.log("Created fallback root element");
    mountOnElement(fallbackRoot);
    return;
  }
  
  mountOnElement(rootElement);
};

const mountOnElement = (element: HTMLElement) => {
  try {
    console.log("Creating React root...");
    const root = ReactDOM.createRoot(element);
    
    console.log("Rendering App component...");
    root.render(
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    );
    
    console.log("App rendered successfully");
  } catch (error) {
    console.error("Critical error rendering app:", error);
    
    // Fallback rendering to show error to user
    element.innerHTML = `
      <div style="font-family: system-ui, sans-serif; padding: 2rem; max-width: 500px; margin: 0 auto; text-align: center;">
        <h2 style="color: #e11d48;">Application Error</h2>
        <p>Sorry, we encountered a problem loading the application.</p>
        <pre style="background: #f1f5f9; padding: 1rem; border-radius: 0.5rem; text-align: left; overflow: auto;">
          ${error instanceof Error ? error.message : 'Unknown error'}
        </pre>
        <button onclick="window.location.reload()" style="background: #3b82f6; color: white; border: none; padding: 0.5rem 1rem; border-radius: 0.25rem; cursor: pointer; margin-top: 1rem;">
          Reload App
        </button>
        <p style="margin-top: 1rem; font-size: 0.875rem; color: #64748b;">
          If the problem persists, please try clearing your browser cache or contact support.
        </p>
      </div>
    `;
  }
};

// Start the app initialization process
initializeApp();
